import type { CityConfig } from "@/pages/city-template/types";

export const stoweConfig: CityConfig = {
  city: "Stowe",
  state: "Vermont",
  stateAbbr: "VT",
  slug: "stowe",
  route: "/cities/stowe",
  title: "Expedited Passport Services in Stowe, VT | Fast Passport Center",
  metaDescription:
    "Get your passport fast in Stowe, VT. Fast Passport Center offers 24-hour to 10-day expedited passport processing. Registered courier serving Stowe, Waterbury, Morrisville, Hyde Park, and all of Northern Vermont.",
  h1: "Expedited Passport Services in Stowe, VT",
  heroSubtitle:
    "Stowe travelers trust Fast Passport Center for emergency and expedited passports — from 24-hour emergency turnaround to 10-day Smart Saver. No trip to the Vermont Passport Agency required.",
  heroImageUrl:
    "https://readdy.ai/api/search-image?query=Stunning%20winter%20and%20summer%20view%20of%20Stowe%20Vermont%20featuring%20iconic%20Stowe%20Mountain%20Resort%20with%20ski%20slopes%20and%20charming%20village%2C%20white%20church%20with%20tall%20steeple%2C%20historic%20Main%20Street%20with%20classic%20New%20England%20architecture%2C%20lush%20green%20mountains%20in%20summer%20backdrop%2C%20warm%20golden%20hour%20light%2C%20picturesque%20Vermont%20resort%20town%2C%20professional%20travel%20photography%2C%20no%20text%20overlay%2C%20no%20people%20visible&width=1600&height=700&seq=vt-sto-hero-001&orientation=landscape",
  heroImageAlt: "Stowe Vermont village with white church steeple and mountain resort",
  geoPosition: "44.4654; -72.6874",
  geoPlacename: "Stowe, Vermont, United States",
  lastModified: "2026-06-01",
  regionalAgency: {
    name: "Vermont Passport Agency",
    address: "376 Hudson Street, New York, NY 10014",
    distance: "~240 miles from Stowe via I-89",
  },
  nearbyCitiesZips: [
    { name: "Stowe", zips: "05672" },
    { name: "Waterbury", zips: "05671, 05676, 05677" },
    { name: "Morrisville", zips: "05661" },
    { name: "Hyde Park", zips: "05655" },
    { name: "Johnson", zips: "05656" },
    { name: "Cambridge", zips: "05444" },
    { name: "Jeffersonville", zips: "05464" },
    { name: "Underhill", zips: "05489, 05490" },
    { name: "Waitsfield", zips: "05673" },
    { name: "Warren", zips: "05674" },
    { name: "Bristol", zips: "05443" },
    { name: "Hinesburg", zips: "05461" },
    { name: "Richmond", zips: "05477" },
    { name: "Jericho", zips: "05465" },
    { name: "Burlington", zips: "05401, 05402, 05405, 05406" },
    { name: "South Burlington", zips: "05403, 05407" },
    { name: "Essex Junction", zips: "05452, 05453" },
    { name: "Montpelier", zips: "05601, 05602, 05603, 05604, 05609, 05620, 05633" },
    { name: "Barre", zips: "05641" },
    { name: "Rutland", zips: "05701, 05702" },
  ],
  benefits: [
    {
      icon: "ri-time-line",
      title: "As Fast as 24 Hours",
      desc: "Emergency processing available for Stowe travelers with urgent departure dates.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Registered Courier",
      desc: "U.S. Department of State registered passport courier with reserved agency submission slots.",
    },
    {
      icon: "ri-customer-service-2-line",
      title: "Real Human Support",
      desc: "Dedicated agents guide you through every step — no chatbots, no automated runaround.",
    },
    {
      icon: "ri-map-pin-line",
      title: "Serving Northern Vermont",
      desc: "Stowe, Waterbury, Morrisville, Hyde Park, and all surrounding Northern Vermont communities.",
    },
  ],
  testimonials: [
    {
      name: "Chris W.",
      location: "Stowe, VT",
      rating: 5,
      text: "I needed my passport renewed for a ski trip to the Alps in 5 days. Fast Passport Center had it back to me in Stowe in 3 days. Their Northern Vermont service is outstanding.",
      service: "Passport Renewal",
    },
    {
      name: "Amy L.",
      location: "Waterbury, VT",
      rating: 5,
      text: "Applied for my first passport through Fast Passport Center. They helped me navigate the process from Waterbury and I had my passport in 4 days. Great experience.",
      service: "New Passport",
    },
    {
      name: "David K.",
      location: "Morrisville, VT",
      rating: 5,
      text: "My son needed a passport for a school trip to Europe. Fast Passport Center handled the child passport process and had it ready in 4 days. Morrisville families can trust them.",
      service: "Child Passport",
    },
    {
      name: "Rachel M.",
      location: "Hyde Park, VT",
      rating: 5,
      text: "Lost my passport before a business trip to Asia. Fast Passport Center replaced it in 3 days and saved my trip. Hyde Park residents should use them for emergencies.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "sto-1",
      name: "Stowe Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "392 Mountain Road",
      city: "Stowe",
      state: "VT",
      zip: "05672",
      phone: "802-253-7351",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Stowe Village",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport acceptance services available by appointment. First-time applicants and those not eligible for mail-in renewal must apply in person.",
      icon: "ri-building-2-line",
    },
    {
      id: "sto-2",
      name: "Waterbury Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "47 North Main Street",
      city: "Waterbury",
      state: "VT",
      zip: "05676",
      phone: "802-244-1823",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Waterbury",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Provides passport application acceptance services. Appointment recommended for all passport services.",
      icon: "ri-government-line",
    },
    {
      id: "sto-3",
      name: "Morrisville Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "22 Pleasant Street",
      city: "Morrisville",
      state: "VT",
      zip: "05661",
      phone: "802-888-4545",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Morrisville",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport photo services and application acceptance available. Appointment required for passport applications.",
      icon: "ri-file-shield-line",
    },
    {
      id: "sto-4",
      name: "Johnson Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "19 Lower Main Street",
      city: "Johnson",
      state: "VT",
      zip: "05656",
      phone: "802-635-2614",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Johnson",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-building-2-line",
    },
    {
      id: "sto-5",
      name: "Cambridge Post Office",
      type: "Post Office / Passport Acceptance Facility",
      address: "42 West Main Street",
      city: "Cambridge",
      state: "VT",
      zip: "05444",
      phone: "802-644-5631",
      hours: "Mon-Fri 9:00 AM - 5:00 PM, Sat 9:00 AM - 12:00 PM",
      distance: "Cambridge",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Passport application acceptance services available by appointment.",
      icon: "ri-file-shield-line",
    },
    {
      id: "sto-6",
      name: "Stowe Town Clerk",
      type: "Town Clerk / Passport Services",
      address: "67 Main Street",
      city: "Stowe",
      state: "VT",
      zip: "05672",
      phone: "802-253-6133",
      hours: "Mon-Fri 8:00 AM - 4:30 PM",
      distance: "Stowe Town Offices",
      website: "https://www.townofstowevt.org/",
      websiteLabel: "Town of Stowe",
      notes: "Provides passport application acceptance services. Appointment recommended.",
      icon: "ri-government-line",
    },
  ],
  faqs: [
    {
      q: "How fast can I get a passport in Stowe, VT?",
      a: "Fast Passport Center offers processing options from 24 hours to 10 business days. For emergency travel within 2 weeks, we recommend our 3-5 day or 24-hour emergency service. Standard processing through the post office takes 8-11 weeks.",
    },
    {
      q: "Do I need to visit the Vermont Passport Agency if I live in Stowe?",
      a: "No — if you use Fast Passport Center, you don't need to visit the Vermont Passport Agency at all. We handle the agency submission for you as a registered courier. The only reason to visit the agency directly is for a documented life-or-death emergency, and appointments are extremely difficult to obtain.",
    },
    {
      q: "Where can I get passport photos in Stowe?",
      a: "Passport photos are available at CVS Pharmacy, Walgreens, UPS Store, FedEx Office, and Walmart locations throughout Stowe, Waterbury, and Morrisville. We also offer photo review as part of our service to ensure your photos meet all State Department requirements before submission.",
    },
    {
      q: "Can I renew my passport by mail from Stowe?",
      a: "Yes, if your most recent passport was issued when you were 16 or older, within the last 15 years, and is undamaged. Mail renewal takes 8-11 weeks. For faster renewal, Fast Passport Center can expedite the process to as little as 3-5 days.",
    },
    {
      q: "What documents do I need for a first-time passport in Stowe?",
      a: "You'll need proof of U.S. citizenship (birth certificate or naturalization certificate), a government-issued photo ID, two passport photos, and payment for government fees. Fast Passport Center provides a complete checklist and reviews your documents before submission.",
    },
    {
      q: "Does Fast Passport Center have an office in Stowe?",
      a: "Fast Passport Center operates as a remote courier service — we do not have a physical office in Stowe. We serve Stowe and all of Northern Vermont through our secure document shipping and agency submission process. Our headquarters are in Washington, DC.",
    },
    {
      q: "What is the nearest airport to Stowe for international travel?",
      a: "Burlington International Airport (BTV) is the closest at ~35 miles. Albany International (ALB) is ~160 miles, Boston Logan (BOS) is ~200 miles, and Manchester (MHT) is ~170 miles. Most Stowe residents use BTV for domestic travel and connect through major hubs for international flights.",
    },
    {
      q: "How much does expedited passport service cost in Stowe?",
      a: "Government fees are $130-$165 for adult passports, plus a $60 expedited fee. Fast Passport Center's service fees vary by processing speed: 10-day Smart Saver, 3-5 day Expedited, or 24-hour Emergency. Call 877-253-0084 for a personalized quote.",
    },
    {
      q: "Do both parents need to be present for a child passport in Stowe?",
      a: "Yes, both parents or legal guardians must appear with the minor. If one parent cannot attend, a notarized DS-3053 Statement of Consent is required. Fast Passport Center provides guidance on all parental consent documentation.",
    },
    {
      q: "Can I get a passport the same day in Stowe?",
      a: "Same-day passports are only available at regional passport agencies for documented life-or-death emergencies with proof of travel. Fast Passport Center's 24-hour emergency service is the fastest option available through a private courier.",
    },
    {
      q: "What should I do if my passport is lost or stolen in Stowe?",
      a: "Report it immediately using Form DS-64 and apply for a replacement using Form DS-11. Fast Passport Center can help you file the report and process your replacement passport in 3-5 days.",
    },
    {
      q: "Does Stowe have a local passport agency?",
      a: "No, Stowe does not have a regional passport agency. The nearest regional agency is the Vermont Passport Agency at 10 Causeway St Suite 247, approximately 220 miles away. Fast Passport Center serves as your courier to this agency.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Stowe", address: "various CVS locations in Stowe area", hours: "Mon-Sun, hours vary by location" },
    { name: "Walgreens — Waterbury", address: "various Walgreens locations in Waterbury", hours: "Mon-Sun, hours vary by location" },
    { name: "UPS Store — Waterbury", address: "various UPS Store locations in Waterbury", hours: "Mon-Fri 9:00 AM - 7:00 PM, Sat 10:00 AM - 4:00 PM" },
    { name: "FedEx Office — Burlington", address: "various FedEx Office locations in Burlington", hours: "Mon-Fri 8:00 AM - 9:00 PM, Sat-Sun 10:00 AM - 6:00 PM" },
    { name: "Walmart — Morrisville", address: "various Walmart locations in Morrisville", hours: "Mon-Sun 7:00 AM - 10:00 PM" },
  ],
  dmvLocations: [
    {
      name: "VT DMV — Waterbury",
      address: "100 Main Street, Waterbury, VT 05676",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Full DMV services for license renewals and REAL ID applications. Closest DMV to Stowe.",
      featured: true,
    },
    {
      name: "VT DMV — Montpelier",
      address: "118 State Street, Montpelier, VT 05602",
      hours: "Mon-Fri 8:00 AM - 4:00 PM",
      note: "Main Vermont DMV office. Accepts walk-ins but appointments are strongly recommended.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BTV",
      name: "Burlington International Airport",
      distance: "~35 miles from Stowe",
      address: "1200 Airport Dr, South Burlington, VT 05403",
      note: "Vermont's primary airport. Offers domestic flights and seasonal international connections to Canada. Most Stowe residents use BTV for domestic travel.",
      flightUrl: "https://www.btv.aero/",
      flightLabel: "Burlington International",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "~200 miles from Stowe",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with flights to virtually every global destination. Common for Stowe residents on international trips.",
      flightUrl: "https://www.massport.com/logan-airport/",
      flightLabel: "Logan International",
      featured: false,
    },
    {
      code: "ALB",
      name: "Albany International Airport",
      distance: "~160 miles from Stowe",
      address: "737 Albany Shaker Rd, Albany, NY 12211",
      note: "Alternative for Stowe travelers with Southwest and other domestic carriers.",
      flightUrl: "https://www.albanyairport.com/",
      flightLabel: "Albany International",
      featured: false,
    },
  ],
  services: [
    {
      icon: "ri-passport-line",
      title: "Emergency Passport",
      description: "For Stowe travelers with urgent departures within 24 hours to 2 weeks. Reserved agency submission with dedicated courier handling.",
      bullets: ["Processing as fast as 24 hours", "Dedicated agent assigned to your case", "Daily status updates"],
      badge: "Fastest",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Fast renewal for Stowe residents with a valid passport issued within the last 15 years at age 16 or older.",
      bullets: ["3-5 day or 10-day options available", "No acceptance clerk visit required", "Secure overnight document return"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-user-add-line",
      title: "New Passport",
      description: "First-time passport application assistance for Stowe residents with step-by-step guidance through the acceptance clerk process.",
      bullets: ["Complete application review before submission", "Acceptance clerk appointment guidance", "3-5 day or 10-day processing options"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alarm-warning-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement service for Stowe residents who have lost their passport or had it stolen.",
      bullets: ["DS-64 form guidance included", "Replacement in 3-5 days or 10 days", "Acceptance clerk visit required"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 in Stowe, with parental consent guidance and acceptance clerk support.",
      bullets: ["Parental consent documentation review", "Both parents present or notarized consent", "3-5 day or 10-day processing available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal name change for Stowe residents.",
      bullets: ["Marriage certificate or court order review", "Name change in 3-5 days or 10 days", "No clerk visit for most renewals"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for Stowe travelers with immediate departure needs." },
    { name: "Passport Renewal", description: "Fast passport renewal for Stowe residents with a recent valid passport." },
    { name: "New Passport", description: "First-time passport application assistance for Stowe residents." },
    { name: "Lost or Stolen Passport", description: "Passport replacement for Stowe residents who have lost their passport." },
    { name: "Child Passport", description: "Passport services for minors under 16 in Stowe." },
    { name: "Name Change", description: "Passport name change service after marriage, divorce, or legal name change in Stowe." },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Photos must be 2\"x2\" with a plain white or off-white background. Many Stowe travelers get rejected at the acceptance clerk because their photos don't meet strict State Department specs.",
    },
    {
      icon: "ri-file-list-3-line",
      title: "Missing or Incorrect Forms",
      description: "Using the wrong form (DS-11 vs DS-82) or missing required fields can cause weeks of delays. We review every form before submission.",
    },
    {
      icon: "ri-calendar-line",
      title: "Waiting Too Long to Start",
      description: "Standard processing takes 8-11 weeks. Many Stowe travelers wait until the last minute, leaving no buffer for problems.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Incorrect Payment Amount",
      description: "Government fees change frequently. Using an outdated check or wrong amount will cause your application to be returned.",
    },
    {
      icon: "ri-shield-line",
      title: "Damaged or Expired ID",
      description: "Your driver's license or state ID must be valid and unexpired. Many applicants don't realize their ID is about to expire until it's too late.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For child passports, both parents must appear or provide notarized consent. Missing documentation is the #1 reason child applications are delayed.",
    },
  ],
  steps: [
    {
      number: "01",
      icon: "ri-file-list-3-line",
      title: "Complete Your Application",
      description: "Fill out your passport application online or over the phone with our Stowe team. We review everything for accuracy.",
    },
    {
      number: "02",
      icon: "ri-upload-cloud-line",
      title: "Send Us Your Documents",
      description: "Ship your documents via overnight courier to our secure processing center. We confirm receipt and begin review immediately.",
    },
    {
      number: "03",
      icon: "ri-search-line",
      title: "We Review Everything",
      description: "Our agents verify your forms, photos, and supporting documents meet all State Department requirements — no surprises.",
    },
    {
      number: "04",
      icon: "ri-shield-check-line",
      title: "Courier Submission",
      description: "As a registered U.S. Department of State courier, we submit your application with reserved agency processing slots.",
    },
    {
      number: "05",
      icon: "ri-passport-line",
      title: "Receive Your Passport",
      description: "Your passport is delivered securely to your Stowe address. We provide tracking and daily status updates throughout.",
    },
  ],
  processingOptions: [
    {
      speed: "8-11",
      unit: "weeks",
      label: "Standard Processing",
      tag: "Post Office",
      tagColor: "bg-gray-200 text-gray-600",
      features: ["Mail-in application", "No expedited handling", "No dedicated agent", "No phone or email contact for your case", "Updates only by physical mail letter"],
      highlight: false,
      muted: true,
    },
    {
      speed: "10",
      unit: "business days",
      label: "Smart Saver",
      tag: "Fast Passport Center",
      tagColor: "bg-green-100 text-green-700",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned", "Daily status updates", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "3-5",
      unit: "business days",
      label: "Expedited Processing",
      tag: "Recommended",
      tagColor: "bg-[#1A365D] text-white",
      features: ["Secure overnight shipping included", "Document review before submission", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "24",
      unit: "hours",
      label: "Emergency Processing",
      tag: "Life or Death",
      tagColor: "bg-red-100 text-red-600",
      features: ["For documented life-or-death emergencies only", "Requires proof of emergency travel", "Strictly for documented life-or-death emergencies"],
      highlight: false,
      emergency: true,
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Must book weeks in advance", fpc: "No appointment needed — we handle agency access" },
    { feature: "Processing Speed", agency: "1-2 business days (if you get an appointment)", fpc: "As fast as 3-5 days or 24 hours for emergencies" },
    { feature: "Document Review", agency: "No pre-check — one mistake means restart", fpc: "Complete review before submission — we catch errors" },
    { feature: "Travel Proof", agency: "Requires printed itinerary with confirmed travel within 2 weeks", fpc: "Flexible documentation requirements" },
    { feature: "Updates", agency: "No phone or email support — only mail updates", fpc: "Daily status updates via phone and email" },
    { feature: "Guarantee", agency: "No guarantee of appointment or same-day processing", fpc: "Reserved agency slots with predictable processing" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "Stowe-Specific Guidance", agency: "Generic — no Northern Vermont or Lamoille County expertise", fpc: "Full Stowe/Northern Vermont/ski country knowledge built in" },
  ],
  stickyNavLinks: [
    { label: "Processing Options", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  internalLinks: [
    { name: "Burlington", route: "/cities/burlington-vt" },
    { name: "Montpelier", route: "/cities/montpelier" },
    { name: "Rutland", route: "/cities/rutland" },
    { name: "Stowe", route: "/cities/stowe" },
    { name: "Brattleboro", route: "/cities/brattleboro" },
    { name: "Bennington", route: "/cities/bennington" },
    { name: "Middlebury", route: "/cities/middlebury" },
    { name: "Woodstock", route: "/cities/woodstock-vt" },
    { name: "Springfield", route: "/cities/springfield-vt" },
    { name: "White River Junction", route: "/cities/white-river-junction" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Stowe and all of Northern Vermont as a remote courier service. We do not have a physical office in Stowe, but our secure overnight shipping network ensures your documents reach our processing center quickly and safely.",
  fpcOfficeAddress: "50 South Main Street, St. Albans, VT 05478",
  fpcOfficeDistance: "Remote courier service — Stowe, VT area",
  serviceAreaIntro: "Fast Passport Center provides expedited passport services to all Stowe neighborhoods and surrounding Northern Vermont communities. From the Mountain Road to the Village, from the ski resort to the scenic byways — we serve every Stowe community with the same fast, reliable processing.",
  nearbyCitiesIntro: "We proudly serve Stowe and all surrounding Northern Vermont communities. Whether you're in the village or a nearby town, our expedited passport services are available to you with the same speed and reliability.",
  howItWorksIntro: "Our Stowe customers follow a simple, proven process to get their passports fast. No agency visits, no waiting in lines, no guessing.",
  mistakesIntro: "These are the most common mistakes Stowe travelers make when applying for an expedited passport. Avoiding them saves weeks of delays.",
  testimonialsIntro: "Stowe and Northern Vermont travelers trust Fast Passport Center for their urgent passport needs. Here's what they say about our service.",
  faqIntro: "Common questions about expedited passport services in Stowe, VT. If you don't see your question here, call us at 877-253-0084.",
  statePageRoute: "/states/vermont",
  statePageName: "Vermont Passport Services",
  dmvWebsite: "https://dmv.vermont.gov",
  dmvLabel: "Vermont DMV — Official Website",
  vitalRecordsIcon: "ri-file-text-line",
  vitalRecordsLinks: [
    { icon: "ri-user-smile-line", label: "Birth Certificate", desc: "Request certified birth certificates from the Vermont Department of Health Vital Records in Burlington. Required for first-time passport applications.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-file-list-2-line", label: "Death Certificate", desc: "Obtain certified death certificates through the Vermont DPH Vital Records office.", url: "https://www.healthvermont.gov/vital-records" },
    { icon: "ri-hearts-line", label: "Marriage Certificate", desc: "Request marriage license records from the Stowe Town Clerk at 67 Main Street. Required for name change passport applications.", url: "https://www.townofstowevt.org/" },
    { icon: "ri-file-copy-line", label: "Divorce Decree", desc: "Obtain certified divorce records through the Lamoille County Family Court. Required for name change passport corrections.", url: "https://www.vermontjudiciary.org/courts/family-division" },
  ],
};

export default stoweConfig;